import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { FiZoomIn, FiZoomOut, FiMaximize2 } from 'react-icons/fi';
import { colors, typography } from '../../theme/colors';
import CycleNode from './CycleNode';

// Layout constants. Tuned so a chain of 5 lanes fits on a 1280px screen
// without horizontal scroll, while still leaving room for ~3 chains stacked.
const LANE_WIDTH = 230;
const LANE_GAP = 28;
const ROW_HEIGHT = 130;
const ROW_GAP = 18;
const NODE_WIDTH = LANE_WIDTH - 16;
const NODE_HEIGHT = ROW_HEIGHT - 14;
const HEADER_HEIGHT = 42;

// Card x = laneIndex * (LANE_WIDTH + LANE_GAP) + 8
// Card y = HEADER_HEIGHT + (rowIndex * (ROW_HEIGHT + ROW_GAP)) + 7

const laneXOffset = (laneIndex) => laneIndex * (LANE_WIDTH + LANE_GAP) + 8;
const rowYOffset = (rowIndex)  => HEADER_HEIGHT + rowIndex * (ROW_HEIGHT + ROW_GAP) + 7;

// Compute (rowIndex per node) by chain — every node in the same chain shares
// a row index, and chains stack vertically. Within a row we still need to
// place nodes in their lanes; if multiple nodes target the same (lane, row)
// cell (e.g. one PO with two GRNs), we offset the duplicates onto sub-rows.
const layoutGraph = (graph, lanes) => {
  const laneIndexById = {};
  lanes.forEach((l, i) => { laneIndexById[l.id] = i; });

  const positions = {};                 // nodeId → { x, y, laneIndex, rowIndex }
  const occupiedCells = new Map();      // `${laneIdx}__${rowIdx}` → count
  let nextRow = 0;

  // Sort chains by root docDate (newest first) so the user sees recent
  // activity at the top.
  const sortedChains = [...graph.chains].sort((a, b) => {
    const an = graph.nodes.find(n => n.id === a.rootId)?.docDate || '';
    const bn = graph.nodes.find(n => n.id === b.rootId)?.docDate || '';
    return bn.localeCompare(an);
  });

  for (const chain of sortedChains) {
    const baseRow = nextRow;
    let maxSubRow = 0;
    for (const nodeId of chain.nodeIds) {
      const node = graph.nodes.find(n => n.id === nodeId);
      if (!node || !node.lane) continue;
      const li = laneIndexById[node.lane];
      if (li == null) continue;
      // Find the next free sub-row in this (lane, base) cell.
      let sub = 0;
      while (true) {
        const cellKey = `${li}__${baseRow + sub}`;
        if (!occupiedCells.has(cellKey)) {
          occupiedCells.set(cellKey, 1);
          positions[nodeId] = {
            x: laneXOffset(li),
            y: rowYOffset(baseRow + sub),
            laneIndex: li,
            rowIndex: baseRow + sub
          };
          if (sub > maxSubRow) maxSubRow = sub;
          break;
        }
        sub += 1;
      }
    }
    nextRow = baseRow + maxSubRow + 1;
  }
  return { positions, totalRows: nextRow };
};

// Build an SVG path from one node's right edge to another node's left edge,
// using a smooth horizontal Bezier curve.
const buildEdgePath = (from, to) => {
  if (!from || !to) return '';
  const x1 = from.x + NODE_WIDTH;
  const y1 = from.y + NODE_HEIGHT / 2;
  const x2 = to.x;
  const y2 = to.y + NODE_HEIGHT / 2;
  const dx = Math.max(40, (x2 - x1) * 0.5);
  return `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`;
};

const edgeColor = (kind, fromNode, toNode, theme) => {
  if (kind === 'return') return theme.error.main;
  if (kind === 'payment') {
    return toNode?.status === 'Cancelled' ? theme.text.tertiary : theme.success.main;
  }
  // Structural — color by completion of `from` node toward `to`. We treat
  // amber (partial) when from-node is still open (Draft), green when posted.
  if (fromNode?.status === 'Cancelled' || toNode?.status === 'Cancelled') return theme.error.main;
  if (fromNode?.status === 'Draft') return theme.warning.main;
  return theme.success.main;
};

const CycleCanvas = ({
  graph,
  selectedNodeId,
  highlightedChainNodeIds,
  onNodeClick,
  onNodeAuditClick,
  onBackgroundClick
}) => {
  const lanes = graph?.lanes || [];
  const { positions, totalRows } = useMemo(() => layoutGraph(graph || { nodes: [], chains: [], edges: [] }, lanes), [graph, lanes]);

  const totalWidth = lanes.length * (LANE_WIDTH + LANE_GAP);
  const totalHeight = HEADER_HEIGHT + Math.max(1, totalRows) * (ROW_HEIGHT + ROW_GAP) + 12;

  // Pan + zoom. Click+drag on background pans; wheel scrolls (browser default
  // for vertical, ctrl+wheel for zoom).
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, panX: 0, panY: 0 });
  const containerRef = useRef(null);

  const onMouseDown = useCallback((e) => {
    // Don't pan when starting drag from a card.
    if (e.target.closest('[data-cycle-card]')) return;
    dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  }, [pan]);

  const onMouseMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    setPan({
      x: dragRef.current.panX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.panY + (e.clientY - dragRef.current.startY)
    });
  }, []);

  const stopDrag = useCallback(() => { dragRef.current.active = false; }, []);

  useEffect(() => {
    window.addEventListener('mouseup', stopDrag);
    return () => window.removeEventListener('mouseup', stopDrag);
  }, [stopDrag]);

  const onWheel = useCallback((e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    setZoom(z => Math.max(0.4, Math.min(2, z + (e.deltaY < 0 ? 0.1 : -0.1))));
  }, []);

  const resetView = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, []);

  // Highlight set — derived from props.
  const highlightSet = useMemo(() => new Set(highlightedChainNodeIds || []), [highlightedChainNodeIds]);
  const dimmingActive = highlightSet.size > 0;

  if (!graph || graph.nodes.length === 0) {
    return (
      <Empty>
        <span>No documents in the selected period.</span>
        <small>Try widening the date range or clearing filters.</small>
      </Empty>
    );
  }

  return (
    <Wrapper>
      <Toolbar>
        <ToolBtn onClick={() => setZoom(z => Math.min(2, z + 0.1))} title="Zoom in"><FiZoomIn /></ToolBtn>
        <ToolBtn onClick={() => setZoom(z => Math.max(0.4, z - 0.1))} title="Zoom out"><FiZoomOut /></ToolBtn>
        <ToolBtn onClick={resetView} title="Reset view"><FiMaximize2 /></ToolBtn>
        <ZoomLabel>{Math.round(zoom * 100)}%</ZoomLabel>
        <Hint>Drag to pan · Ctrl+Scroll to zoom</Hint>
      </Toolbar>

      <Viewport
        ref={containerRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onWheel={onWheel}
        onClick={(e) => {
          // Clear selection on clicks anywhere in the canvas that aren't on a
          // card. Lane headers, rails, and the SVG edge layer all qualify as
          // "background" — the user just wants to deselect.
          if (!e.target.closest('[data-cycle-card]')) {
            onBackgroundClick && onBackgroundClick();
          }
        }}
      >
        <Stage
          style={{
            width: totalWidth,
            height: totalHeight,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`
          }}
        >
          {/* Lane headers + vertical dividers */}
          {lanes.map((lane, i) => (
            <LaneHeader key={lane.id} style={{ left: laneXOffset(i), width: LANE_WIDTH }}>
              {lane.label}
            </LaneHeader>
          ))}
          {lanes.map((lane, i) => (
            <LaneRail
              key={`rail-${lane.id}`}
              style={{
                left: laneXOffset(i),
                width: LANE_WIDTH,
                top: HEADER_HEIGHT,
                height: totalHeight - HEADER_HEIGHT
              }}
            />
          ))}

          {/* Edges (SVG layer underneath the cards) */}
          <EdgeLayer style={{ width: totalWidth, height: totalHeight }}>
            <defs>
              <marker id="cycle-arrow-green" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill={colors.success.main} />
              </marker>
              <marker id="cycle-arrow-red" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill={colors.error.main} />
              </marker>
              <marker id="cycle-arrow-amber" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill={colors.warning.main} />
              </marker>
              <marker id="cycle-arrow-grey" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill={colors.text.tertiary} />
              </marker>
            </defs>
            {graph.edges.map((edge, idx) => {
              const fromPos = positions[edge.from];
              const toPos = positions[edge.to];
              if (!fromPos || !toPos) return null;
              const fromNode = graph.nodes.find(n => n.id === edge.from);
              const toNode = graph.nodes.find(n => n.id === edge.to);
              const c = edgeColor(edge.kind, fromNode, toNode, colors);
              const dimmed = dimmingActive && (!highlightSet.has(edge.from) || !highlightSet.has(edge.to));
              const markerName = c === colors.success.main ? 'green'
                : c === colors.error.main ? 'red'
                : c === colors.warning.main ? 'amber'
                : 'grey';
              return (
                <path
                  key={`e-${idx}`}
                  d={buildEdgePath(fromPos, toPos)}
                  stroke={c}
                  strokeWidth={edge.kind === 'return' ? 1.5 : 2}
                  strokeDasharray={edge.kind === 'return' ? '4 3' : 'none'}
                  fill="none"
                  opacity={dimmed ? 0.18 : 0.85}
                  markerEnd={`url(#cycle-arrow-${markerName})`}
                />
              );
            })}
          </EdgeLayer>

          {/* Nodes */}
          {graph.nodes.map(node => {
            const pos = positions[node.id];
            if (!pos) return null;
            const dimmed = dimmingActive && !highlightSet.has(node.id);
            return (
              <div
                key={node.id}
                data-cycle-card
                style={{
                  position: 'absolute',
                  transform: `translate(${pos.x}px, ${pos.y}px)`,
                  width: NODE_WIDTH,
                  height: NODE_HEIGHT
                }}
              >
                <CycleNode
                  node={node}
                  width={NODE_WIDTH}
                  height={NODE_HEIGHT}
                  selected={selectedNodeId === node.id}
                  highlighted={highlightSet.has(node.id) && selectedNodeId !== node.id}
                  dimmed={dimmed}
                  onClick={onNodeClick}
                  onAuditClick={onNodeAuditClick}
                />
              </div>
            );
          })}
        </Stage>
      </Viewport>
    </Wrapper>
  );
};

export default CycleCanvas;

// ── styled ──────────────────────────────────────────────────────────────────
const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 480px;
  background: ${colors.background?.paper || '#fafafa'};
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
  overflow: hidden;
`;
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-bottom: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  background: white;
  z-index: 10;
`;
const ToolBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px; height: 26px;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  background: white;
  border-radius: 4px;
  cursor: pointer;
  color: ${colors.text.secondary};
  &:hover { color: ${colors.primary.main}; border-color: ${colors.primary.main}; }
`;
const ZoomLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  font-family: ${typography.fontFamily.mono};
  color: ${colors.text.primary};
  margin-left: 6px;
`;
const Hint = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: ${colors.text.secondary};
`;
const Viewport = styled.div`
  flex: 1;
  position: relative;
  overflow: auto;
  cursor: grab;
  user-select: none;
  &:active { cursor: grabbing; }
`;
const Stage = styled.div`
  position: relative;
  transform-origin: 0 0;
  transition: transform 0.05s linear;
`;
const LaneHeader = styled.div`
  position: absolute;
  top: 0;
  height: ${HEADER_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: ${colors.text.primary};
  background: white;
  border-bottom: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-right: 1px dashed ${colors.neutral?.[100] || '#f3f4f6'};
`;
const LaneRail = styled.div`
  position: absolute;
  background: ${({ theme: _t }) => 'transparent'};
  border-right: 1px dashed ${colors.neutral?.[100] || '#f3f4f6'};
  pointer-events: none;
`;
const EdgeLayer = styled.svg.attrs({ xmlns: 'http://www.w3.org/2000/svg' })`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
`;
const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: ${colors.text.primary};
  font-size: 15px;
  font-weight: 600;
  gap: 8px;
  border: 1px dashed ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
  background: ${colors.background?.paper || '#fafafa'};

  small {
    color: ${colors.text.secondary};
    font-size: 13px;
    font-weight: 500;
  }
`;
